import moment from "moment";

import { IPatientAppointmentModel as MyleInterfacesPatientIPatientAppointmentModel } from "@/Appointments/Models/UI/IPatientAppointmentModel";
import { Individual } from "@/Common/Api/Individuals/Dto/Individual";
import { IConsentProgramHistory } from "@/Common/Api/Patient/Dto/IConsentProgramHistory";
import { Patient as MyleModelsPatientResponsePatient } from "@/Common/Api/Patient/Dto/Patient";
import { PatientConsentModel } from "@/Common/Api/Patient/Dto/PatientConsentModel";
import { Pharmacy } from "@/Common/Api/Pharmacy/Dto/Responses/Pharmacy";
import { DateTime } from "@/Common/Helpers/DateTime";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { ConsentPrograms, ConsentProgramsData } from "@/Common/Models/ConsentPrograms";
import { MyleTabKey } from "@/Common/Models/MyleMenu/enums/MyleTabKey";
import { Patient as MyleModelsPatientPatient } from "@/Common/Models/Patient/Patient";
import { EditMode } from "@/Common/Models/Patient/PatientEnums";
import { PatientIdentifier } from "@/Common/Models/Patient/PatientIdentifier";
import { PatientPossibleWarningDto } from "@/Common/Models/Patient/PatientPossibleWarningDto";
import { PatientRefIntervenorWithDetailsModel } from "@/Common/Models/Patient/PatientRefIntervenorWithDetailsModel";
import { IPatientBannerModel } from "@/Common/Models/Patient/UI/IPatientBannerModel";
import { IPatientCliniciansModel } from "@/Common/Models/Patient/UI/Profile/IPatientCliniciansModel";
import { IPatientContactsModel } from "@/Common/Models/Patient/UI/Profile/IPatientContactsModel";
import { IPatientDemographicsModel } from "@/Common/Models/Patient/UI/Profile/IPatientDemographicsModel";
import { IPatientIdentifiersModel } from "@/Common/Models/Patient/UI/Profile/IPatientIdentifiersModel";
import { IPatientOtherInformationModel } from "@/Common/Models/Patient/UI/Profile/IPatientOtherInformationModel";
import { IPatientRelationshipsModel } from "@/Common/Models/Patient/UI/Profile/IPatientRelationshipsModel";
import { RAMQInfo } from "@/Common/Models/RAMQ/RAMQInfo";
import { AllowlistType } from "@/Common/Models/UI/Enums";
import { ProfilePicture } from "@/Common/Models/UI/ProfilePicture";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { ServerEvent, ServerEventWithThen } from "@/Common/Stores/Actions/ServerEvent";
import { BaseServerOperationHandler } from "@/Common/Stores/Handlers/Server/BaseServerOperationHandler";
import { PharmacyProxy } from "@/ContactDirectory/Models/PharmacyProxy";
import { DocumentsHelper } from "@/Documents/Helpers/DocumentsHelper";
import { IDocumentCreateEditUIModel } from "@/Documents/Models/UI/IDocumentCreateEditUIModel";
import { LabsHelper } from "@/Labs/Helpers/LabsHelper";
import { PatientPrescriptionTab } from "@/Patient/Components/Medication/PatientPrescriptionTab";
import { PatientModelHelper } from "@/Patient/Helpers/PatientModelHelper";
import { IPatientAppointmentModel as MyleInterfacesPatientPatientAppointmentIPatientAppointmentModel } from "@/Patient/Models/UI/Appointment/IPatientAppointmentModel";
import { IPatientCommunicationsModel } from "@/Patient/Models/UI/History/IPatientCommunicationsModel";
import { IPatientMessagesModel } from "@/Patient/Models/UI/History/IPatientMessagesModel";
import { IPatientPrivateBillModel } from "@/Patient/Models/UI/History/IPatientPrivateBillModel";
import { IPatientRecallsModel } from "@/Patient/Models/UI/History/IPatientRecallsModel";
import { IPatientTasksModel } from "@/Patient/Models/UI/History/IPatientTasksModel";
import { IPatientUIModel } from "@/Patient/Models/UI/IPatientUIModel";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientProfileResponseHandler extends BaseServerOperationHandler {
    constructor(action: BaseAction) {
        super(action);
        this.RegisterOperation(ServerOperations.Server.Myle_LoadPatient, this.loadPatientAndRedirect, SubServerOperations.Patient.LoadPatientAndRedirect);
        this.RegisterOperation(ServerOperations.Server.Myle_LoadPatient, this.loadPatientWithoutRedirecting, SubServerOperations.Patient.LoadPatientWithoutRedirecting);
        this.RegisterOperation(ServerOperations.Server.Myle_LoadProtectedPatient, this.loadProtectedPatientAndSetActivePatient, SubServerOperations.Patient.SetActivePatient);
        this.RegisterOperation(ServerOperations.Server.Myle_LoadProtectedPatient, this.loadProtectedPatient);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Demographics_Update, this.demographicsUpdate);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Identifiers_Update, this.identifiersUpdate);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_OtherInfo_Update, this.otherInfoUpdate);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Memo_Save, this.memoUpdate);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Clinicians_Update, this.cliniciansUpdate);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Contacts_Update, this.contactsUpdate);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Relationships_Update, this.relationshipsUpdate);
        this.RegisterOperation(ServerOperations.Server.RAMQ_CheckSubscription, this.loadPatientRamqInfo);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_GenerateLabel, this.openLabelPdf);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_CPP_GeneratePDF, this.printCpp);
        this.RegisterOperation(ServerOperations.Server.Myle_SaveProfileImage, this.saveProfileImageComplete, SubServerOperations.Profile.SaveProfileImage);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Deactivate, this.deactivatePatientComplete);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Reactivate, this.reactivatePatientComplete);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MarkTestStatus, this.markTestPatientComplete);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Enrollment_GenerateRAMQForm, this.generateRamqForm);
        this.RegisterOperation(ServerOperations.Server.Myle_LoadConsentPrograms, this.loadConsentProgramsForAdd, SubServerOperations.Profile.LoadForAdd);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_AddConsentProgramHistory, this.addConsentProgram);
        this.RegisterOperation(ServerOperations.Server.Myle_LoadConsentPrograms, this.loadConsentProgramsForEdit, SubServerOperations.Profile.LoadForEdit);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_UpdateConsentProgram, this.updateConsentProgram);
        this.RegisterOperation(ServerOperations.Server.DeleteProfilePicEmployee, this.deleteProfilePic);
        this.RegisterOperation(ServerOperations.Server.Myle_PrintMultipleFetchUrlIntoSingleFile, this.handlePrint);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_LoadConsentProgramHistory, this.loadConsentProgramHistory);
    }

    private loadPatientAndRedirect(response: ServerEventWithThen<any, () => void>, store: PatientStore): void {
        this.loadPatient(response, store, true);
    }

    private loadPatientWithoutRedirecting(response: ServerEventWithThen<any, () => void>, store: PatientStore): void {
        this.loadPatient(response, store, false);
    }

    private loadProtectedPatientAndSetActivePatient(response: ServerEventWithThen<any, () => void>, store: PatientStore): void {
        this.handleLoadProtectedPatient(response, store, true);
    }

    private loadProtectedPatient(response: ServerEventWithThen<any, () => void>, store: PatientStore): void {
        this.handleLoadProtectedPatient(response, store, false);
    }

    private handleLoadProtectedPatient(response: ServerEventWithThen<any, () => void>, store: PatientStore, setActivePatientUI: boolean = false): void {
        if (response.hasErrors()) {
            store.IsLoading = false;

            if (response.Data?.["Status"] == 403) {
                const patientId = response.requestDto?.["IndividualId"];
                store.UnlockPatientFile(patientId);
                store.addPatientIdDeniedToCurrentUser(patientId);
            }

            return;
        }

        store.removePatientIdDeniedToCurrentUser(response.PatientId);
        this.loadPatient(response, store, false, setActivePatientUI);
    }

    private loadPatient(response: ServerEventWithThen<any, () => void>, store: PatientStore, redirect: boolean = false, setActivePatientUI: boolean = false): void {
        sessionStore.hideGlobalOverlay();

        const patient = MyleModelsPatientPatient.FromResponse(response);
        if (setActivePatientUI)
            store.setCurrentPatientContactId(patient.IndividualResponse.Contact_id);

        if (patient.possiblePatientWarning?.length > 0) {
            sessionStore.dispatchMain(
                LocalOperations.MainComponentViewOperations.DisplayPatientWarnings,
                {
                    patientContactId: patient.IndividualResponse.Contact_id,
                    warnings: patient.possiblePatientWarning,
                    callback: (dto: PatientPossibleWarningDto) => {
                        if (dto.SexAtBirthLookupId)
                            patient.IndividualResponse.GenderLookupId = dto.SexAtBirthLookupId;

                        this.rawLoadPatient(response, patient, store, redirect);
                    }
                }
            );
        }
        else {
            this.rawLoadPatient(response, patient, store, redirect);
        }
    }

    private rawLoadPatient(response: ServerEventWithThen<any, () => void>, patient: MyleModelsPatientPatient, store: PatientStore, redirect: boolean = false): void {
        const patientContactId: string = patient.IndividualResponse.Contact_id;
        store.PatientModels[patientContactId] = patient;
        const warningList: string[] = [];

        store.ActionsProfile.loadRamqInfo(patient);
        const uiModel: IPatientUIModel = store.getDefaultPatientUIModel(patient);
        const profileModel: IPatientDemographicsModel = patient.ToPatientDemographicsModel();
        const contactsModel: IPatientContactsModel = patient.ToPatientContactsModel();
        const idModel: IPatientIdentifiersModel = patient.ToPatientIdentifiersModel();
        const cliniciansModel: IPatientCliniciansModel = patient.ToPatientCliniciansModel();
        const otherInformationModel: IPatientOtherInformationModel = patient.ToPatientOtherInformationModel();
        const relationshipsModel: IPatientRelationshipsModel = patient.ToPatientRelationshipsModel();
        store.PatientUIModels[patientContactId] = uiModel;

        const patientBannerDefault = {
            Model: null,
            PatientDropdownVisible: false,
            Warnings: []
        };

        let patientBannerModel: IPatientBannerModel = Immutable.fromJS(patientBannerDefault);
        patientBannerModel = patientBannerModel.delete("Model");
        patientBannerModel = patientBannerModel
            .set("Model", patient)
            .set("ProfileImageVersion", GuidHelper.NewGuid())
            .set("IsPartOfMergeRequest", response.Data.IsPartOfMergeRequest);

        store.PatientBannerModels[patientContactId] = patientBannerModel;

        store.PatientProfileDemographicModels[patientContactId] = profileModel;
        store.PatientProfileContactsModels[patientContactId] = contactsModel;
        store.PatientProfileIdentifiersModels[patientContactId] = idModel;
        store.PatientProfileCliniciansModels[patientContactId] = cliniciansModel;
        store.PatientProfileOtherIdentificationModels[patientContactId] = otherInformationModel;
        store.PatientProfileRelationshipsModels[patientContactId] = relationshipsModel;
        store.PatientProfileEnrollmentModels[patientContactId] = Immutable.fromJS({});
        store.PatientPortalOverviewModels[patientContactId] = Immutable.fromJS(PatientHelper.getDefaultPatientPortalManagementModel());
        // create doc model
        const newDocModel: IDocumentCreateEditUIModel = Immutable.fromJS(DocumentsHelper.DefaultModelForDocumentCreateEditUIModel());
        const editDocModel: IDocumentCreateEditUIModel = Immutable.fromJS(DocumentsHelper.DefaultModelForDocumentCreateEditUIModel());

        const documentModel = DocumentsHelper.DefaultModelForDocumentsModel();
        documentModel.IsFromPatient = true;
        documentModel.PatientContactId = patientContactId;
        documentModel.NewDocument = newDocModel;
        documentModel.SelectedDocument = editDocModel;

        store.PatientDocuments[patientContactId] = Immutable.fromJS(documentModel);

        store.PatientLabs[patientContactId] = LabsHelper.BuildDefaultLabModelsForPatientProfile(patientContactId);

        const historyPrivateBillModel: IPatientPrivateBillModel = Immutable.fromJS(PatientModelHelper.getDefaultPatientPrivateBillModel(cacheStore.Clinicians));
        store.PatientHistoryBillsModels[patientContactId] = historyPrivateBillModel;

        const historyMessageModel: IPatientMessagesModel = PatientModelHelper.setDefaultHistoryMessageModel();
        const historyTaskModel: IPatientTasksModel = PatientModelHelper.setDefaultHistoryTaskModel();
        const historyCommunicationsModel: IPatientCommunicationsModel = PatientModelHelper.setDefaultHistoryCommunicationsModel();
        let historyRecallsModel: IPatientRecallsModel = Immutable.fromJS(PatientModelHelper.getDefaultPatientRecallsModel());
        // set data
        const statusLookups = PatientModelHelper.getStatusesLookupsFromCache();

        historyRecallsModel = historyRecallsModel.set("Statuses", Immutable.List(statusLookups));

        const hasFutureRecalls = this.verifyPatientHasFutureRecalls(patient);
        if (hasFutureRecalls) {
            warningList.push("HasPendingRecallsLabel"); // we push dictionary keys and message will be loaded from cacheStore - this way if we change language, changes will reflect without need to reload patient
        }

        store.PatientHistoryMessagesModels[patientContactId] = historyMessageModel;
        store.PatientHistoryTasksModels[patientContactId] = historyTaskModel;
        store.PatientHistoryCommunicationsModels[patientContactId] = historyCommunicationsModel;
        store.PatientHistoryRecallsModels[patientContactId] = historyRecallsModel;

        this.loadPatientMedicalItems(patientContactId, store);
        store.IsLoading = false;

        if (redirect) {
        // Set open file
            sessionStore.dispatch(LocalOperations.Person.OpenFile, { person: patientStore.getPersonModel(patientContactId) });

            // enable patient tab and open it
            cacheStore.UpdateTab(MyleTabKey.Patients);
            // this simply redirects to #patient
            store.showPatientTab();
        }

        if (!cacheStore.ClinicSettings.EnableConsentProgramHistory) {
            store.ActionsProfile.loadConsentProgramHistory(patientContactId);
        }

        sessionStore.dispatch(LocalPatientOperations.Profile.OtherInfo_LoadPreferredDirectoryPharmacy, { patientContactId: patientContactId });
        sessionStore.dispatch(LocalPatientOperations.Profile.Clinicians_LoadHealthCareProviders, { patientContactId: patientContactId });

        if (response.then) {
            response.then();
        }
    }

    private loadPatientMedicalItems(patientContactId: string, store: PatientStore): void {
        let historyAppointmentModel: MyleInterfacesPatientIPatientAppointmentModel = Immutable.fromJS(PatientModelHelper.getDefaultHistoryPatientAppointmentModel());
        // set data properly
        historyAppointmentModel = historyAppointmentModel.set("Sites", Immutable.List(cacheStore.SiteCache));
        historyAppointmentModel = historyAppointmentModel.set("AppointmentStates", Immutable.List(cacheStore.GetAppointmentStateLookups()));

        store.PatientHistoryAppointmentsModels[patientContactId] = historyAppointmentModel;

        // patient appointment tabs ... TODO  deal with this for real:
        const defaultAppointmentModelJs = PatientModelHelper.getDefaultPatientAppointmentModel();
        const patientAppointments: MyleInterfacesPatientPatientAppointmentIPatientAppointmentModel = Immutable.fromJS(defaultAppointmentModelJs);
        store.PatientAppointmentModels[patientContactId] = patientAppointments;

        store.PatientPrescriptionTabModels[patientContactId] = Immutable.fromJS(PatientPrescriptionTab.GetDefaultPatientPrescriptionTabModel());
        store.PatientMedicationTabModels[patientContactId] = Immutable.fromJS(PatientModelHelper.getDefaultPatientMedicationTabModel());
        store.PatientMedicationGridModels[patientContactId] = Immutable.fromJS(PatientModelHelper.getPatientMedicationGridModel());
    }

    private verifyPatientHasFutureRecalls(patient: MyleModelsPatientPatient): boolean {
        return patient.RelatedRecalls.filter(recall => {
            if (recall.DueDate == null || recall.DueDate === "") return false;
            else {
                return moment(recall.DueDate).isSameOrAfter(new Date());
            }
        }).length > 0;
    }

    private memoUpdate(response: ServerEvent, store: PatientStore): void {
        var uiModel = store.PatientUIModels[response.PatientId];
        var patientModel = store.PatientModels[response.PatientId];

        patientModel.IndividualResponse = response.Data["Individual"];
        uiModel = uiModel.set("IsProfileMemoEditMode", false);
        uiModel = uiModel.set("ShowProfileMemoPopup", false);

        sessionStore.hideGlobalOverlay();
        store.PatientUIModels[response.PatientId] = uiModel;
        store.PatientModels[response.PatientId] = patientModel;
    }

    private demographicsUpdate(response: ServerEvent, store: PatientStore): void {
        const updatedIndividual: Individual = response.Data["Individual"];
        const updatedPatient: MyleModelsPatientResponsePatient = response.Data["Patient"];

        // refresh current patient models with freshly updated ones
        const patientModel = store.PatientModels[response.PatientId];
        patientModel.IndividualResponse = {
            ...updatedIndividual,
            ContactFacetId: patientModel?.IndividualResponse?.ContactFacetId
        };
        patientModel.PatientResponse = updatedPatient;
        const uiModel = store.PatientUIModels[response.PatientId]
            .set("PatientProfileEditMode", EditMode.View)
            .set("LockPatientDemographicSaveButton", false);

        const bannerModel = store.PatientBannerModels[response.PatientId]
            .delete("Model")
            .set("Model", patientModel);
        store.PatientBannerModels[response.PatientId] = bannerModel;
        store.PatientProfileDemographicModels[response.PatientId] = patientModel.ToPatientDemographicsModel();
        store.PatientUIModels[response.PatientId] = uiModel;
        store.PatientModels[response.PatientId] = patientModel;
    }

    private identifiersUpdate(response: ServerEvent, store: PatientStore): void {
        var success: boolean = response.Data["IsSuccess"];
        if (success) {
            var updatedHealthCards: PatientIdentifier[] = response.Data["HealthCards"];
            var updatedOtherIdentifiers: PatientIdentifier[] = response.Data["OtherIdentifiers"];

            // refresh current patient models with freshly updated ones
            var uiModel = store.PatientUIModels[response.PatientId];
            var patientModel = store.PatientModels[response.PatientId];

            // update current patient model and update patient banner model
            patientModel.HealthCards = updatedHealthCards;
            patientModel.OtherPatientIdentifiers = updatedOtherIdentifiers;
            patientModel.PatientResponse.Identifier01 = response.Data["Identifier01"];
            patientModel.PatientResponse.Identifier02 = response.Data["Identifier02"];
            patientModel.PatientResponse.Identifier03 = response.Data["Identifier03"];

            patientModel.IndividualResponse.DateOfBirth = response.Data["DDN"];

            if (updatedHealthCards && updatedHealthCards.length > 0) {
                patientModel.IndividualResponse.NAM = updatedHealthCards[0].Number;
            }
            else {
                patientModel.IndividualResponse.NAM = "";
            }

            uiModel = uiModel.set("PatientProfileEditMode", EditMode.View);

            var newIDModel = patientModel.ToPatientIdentifiersModel();

            var bannerModel = store.PatientBannerModels[response.PatientId];
            bannerModel = bannerModel.delete("Model");
            bannerModel = bannerModel.set("Model", patientModel);
            store.PatientBannerModels[response.PatientId] = bannerModel;
            store.PatientProfileIdentifiersModels[response.PatientId] = newIDModel;
            store.PatientModels[response.PatientId] = patientModel;
            store.PatientUIModels[response.PatientId] = uiModel;
        }
    }

    private otherInfoUpdate(response: ServerEvent, store: PatientStore): void {
        const patientModel = store.PatientModels[response.PatientId];
        let uiModel = store.PatientUIModels[response.PatientId];

        if (patientModel) {
            patientModel.PreferredPharmacy = new PharmacyProxy({
                customPharmacy: response.Data["PreferredPharmacy"] as Pharmacy,
                directoryPharmacyId: response.Data["PreferredDirectoryPharmacyId"] as string
            });
            patientModel.InsuranceCompany = response.Data["InsuranceDetail"];

            var newOtherInfoModel = patientModel.ToPatientOtherInformationModel();

            store.PatientProfileOtherIdentificationModels[response.PatientId] = Immutable.fromJS(newOtherInfoModel);
            store.PatientProfileOtherIdentificationUndoModels[response.PatientId] = Immutable.fromJS(newOtherInfoModel);
        }

        uiModel = uiModel.set("PatientProfileEditMode", EditMode.View);

        store.PatientModels[response.PatientId] = patientModel;
        store.PatientUIModels[response.PatientId] = uiModel;

        store.ActionsProfile.loadConsentProgramHistory(response.PatientId);
        sessionStore.dispatch(LocalPatientOperations.Profile.OtherInfo_LoadPreferredDirectoryPharmacy, { patientContactId: response.PatientId });
    }

    private cliniciansUpdate(response: ServerEvent, store: PatientStore): void {
        var uiModel = store.PatientUIModels[response.PatientId];
        var patientModel = store.PatientModels[response.PatientId];

        if (patientModel) {
            patientModel.IntervenorsOnFile = response.Data.patientRefIntervenors?.map(patientRefIntervenor => new PatientRefIntervenorWithDetailsModel(patientRefIntervenor)) ?? null;
            const newCliniciansModel = patientModel.ToPatientCliniciansModel().set("PatientFileAllowlist", response.Data.PatientFileAllowlist ?? {
                AllowlistType: AllowlistType.PatientFileAllowlist,
                PatientContactFacetId: patientModel?.IndividualResponse?.ContactFacetId,
                MemberIds: [],
                IsActive: false
            });
            store.PatientProfileCliniciansModels[response.PatientId] = Immutable.fromJS(newCliniciansModel);
            store.PatientProfileCliniciansUndoModels[response.PatientId] = Immutable.fromJS(newCliniciansModel);
        }

        uiModel = uiModel.set("PatientProfileEditMode", EditMode.View);

        store.PatientModels[response.PatientId] = patientModel;
        store.PatientUIModels[response.PatientId] = uiModel;

        // update the labs banner, needs refactoring after ontario is released
        // check the lab's patient is the same one from the update
        if (labsStore.LabModel.get("MylePatient")?.IndividualResponse?.ContactFacetId == patientModel?.IndividualResponse?.ContactFacetId)
            labsStore.LabModel = labsStore.LabModel.set("MylePatient", patientModel);

        sessionStore.dispatch(LocalPatientOperations.Profile.Clinicians_LoadHealthCareProviders, { patientContactId: response.PatientId });
    }

    private contactsUpdate(response: ServerEvent, store: PatientStore): void {
        store.updateContacts(response.Data);
    }

    private relationshipsUpdate(response: ServerEvent, store: PatientStore): void {
        var relationships = response.Data["Relationships"];
        var uiModel = store.PatientUIModels[response.PatientId];
        var patientModel = store.PatientModels[response.PatientId];

        if (response.Errors != null && response.Errors.length > 0) {
            sessionStore.showPopup(response.Errors);
            uiModel = uiModel.set("PatientProfileEditMode", EditMode.Edit);
            store.PatientUIModels[response.PatientId] = uiModel;
            return;
        }

        if (patientModel) {
            patientModel.Relationships = relationships;

            var newRelationshipsModel = patientModel.ToPatientRelationshipsModel();
            var newContactModel = patientModel.ToPatientContactsModel();
            var newDemographicModel = patientModel.ToPatientDemographicsModel();
            var newIdentifierModel = patientModel.ToPatientIdentifiersModel();

            newDemographicModel = newDemographicModel.set("MothersFirstName", response.Data["MotherFirstName"]);
            newDemographicModel = newDemographicModel.set("MothersLastName", response.Data["MotherLastName"]);
            newDemographicModel = newDemographicModel.set("FathersFirstName", response.Data["FatherFirstName"]);
            newDemographicModel = newDemographicModel.set("FathersLastName", response.Data["FatherLastName"]);

            store.PatientProfileIdentifiersModels[response.PatientId] = Immutable.fromJS(newIdentifierModel);
            store.PatientProfileDemographicModels[response.PatientId] = Immutable.fromJS(newDemographicModel);
            store.PatientProfileContactsModels[response.PatientId] = Immutable.fromJS(newContactModel);

            store.PatientProfileRelationshipsModels[response.PatientId] = Immutable.fromJS(newRelationshipsModel);
            store.PatientProfileRelationshipsUndoModels[response.PatientId] = Immutable.fromJS(newRelationshipsModel);
        }

        if (response.Data["StayInEditMode"] != true)
            uiModel = uiModel.set("PatientProfileEditMode", EditMode.View);

        store.PatientModels[response.PatientId] = patientModel;
        store.PatientUIModels[response.PatientId] = uiModel;
    }

    private loadPatientRamqInfo(response: ServerEvent, store: PatientStore): void {
        // read data
        let data = new RAMQInfo().FromResponse(response);

        // update models
        let patient = store.PatientModels[response.PatientId];
        patient.setRamq(data);

        let enrollmentModel = patient.ToPatientEnrollmentModel();
        let uiModel = store.PatientUIModels[response.PatientId].set("PatientProfileEditMode", EditMode.View);

        // update store
        store.PatientModels[response.PatientId] = patient;
        store.PatientUIModels[response.PatientId] = uiModel;
        store.PatientProfileEnrollmentModels[response.PatientId] = Immutable.fromJS(enrollmentModel);
        store.PatientProfileEnrollmentUndoModels[response.PatientId] = Immutable.fromJS(enrollmentModel);
    }

    private openLabelPdf(response: ServerEvent): void {
        var fileName = response.Data["FileName"];
        PrintHelper.ShowPrintPreviewFileName(fileName);
    }

    private printCpp(response: ServerEvent): void {
        PrintHelper.ShowPrintPreviewFileName(response.Data["FileName"]);
    }

    private saveProfileImageComplete(response: ServerEvent, store: PatientStore): void {
        var pic: ProfilePicture = response.Data.ProfilePicture;
        var uiModel = store.PatientUIModels[response.PatientId];
        var bannerModel = store.PatientBannerModels[response.PatientId];
        var model = bannerModel.get("Model");
        model.ProfilePicture = pic;
        bannerModel = bannerModel
            .set("Model", model)
            .set("ProfileImageVersion", GuidHelper.NewGuid());

        uiModel = uiModel.set("ShowProfileImagePopup", false);
        store.PatientBannerModels[response.PatientId] = bannerModel;
        store.PatientUIModels[response.PatientId] = uiModel;
        sessionStore.hideGlobalOverlay();
    }

    private generateRamqForm(response: ServerEvent): void {
        var fileName = response.Data["FileName"];
        PrintHelper.ShowPrintPreviewFileName(fileName);
    }

    private reactivatePatientComplete(response: ServerEvent, store: PatientStore): void {
        var contactId = response.Data["PatientContactId"];
        var patientModel = patientStore.PatientModels[contactId];

        if (patientModel) {
            patientModel.InactivableResponse.InactivationNote = "";
            patientModel.InactivableResponse.IsInactive = false;
            patientModel.InactivableResponse.ReasonForInactivityLookupId = null;
        }

        var uiModel = patientStore.PatientUIModels[contactId];
        if (uiModel) {
            uiModel = uiModel.set("ShowDeactivatePopup", false);
        }
        sessionStore.hideGlobalOverlay();

        window.setTimeout(() => {
            store.IsLoading = true;
            patientStore.ActionsProfile.loadPatient(contactId);
        }, 0);
    }

    private deactivatePatientComplete(response: ServerEvent, store: PatientStore): void {
        var contactId = response.Data["PatientContactId"];
        var reasonId = response.Data["ReasonForInactivityLookupId"];
        var note = response.Data["DeactivationNote"];

        var patientModel = patientStore.PatientModels[contactId];

        if (patientModel) {
            patientModel.InactivableResponse.InactivationNote = note;
            patientModel.InactivableResponse.IsInactive = true;
            patientModel.InactivableResponse.ReasonForInactivityLookupId = reasonId;
        }

        var uiModel = patientStore.PatientUIModels[contactId];
        if (uiModel) {
            uiModel = uiModel.set("ShowDeactivatePopup", false);
        }
        sessionStore.hideGlobalOverlay();

        window.setTimeout(() => {
            store.IsLoading = true;
            patientStore.ActionsProfile.loadPatient(contactId);
        }, 0);
    }

    private markTestPatientComplete(response: ServerEvent, store: PatientStore): void {
        let contactId = response.Data["PatientContactId"];

        let uiModel = patientStore.PatientUIModels[contactId];
        if (uiModel) {
            uiModel.set("ShowMarkTestPatientPopup", false);
        }
        overlayStore.hideOverlay("global-overlay");
        window.setTimeout(() => {
            store.IsLoading = true;
            patientStore.ActionsProfile.loadPatient(contactId);
        }, 0);
    }

    private loadConsentProgramsForAdd(response: ServerEvent, store: PatientStore): void {
        let consentProgramsResponse: ConsentProgramsData[] = response.Data["ConsentPrograms"];
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[response.PatientId];

        if (consentProgramsResponse.length > 0) {
            let consentPrograms = consentProgramsResponse.map(c => new ConsentPrograms(c));
            otherInfoModel = otherInfoModel.set("ConsentPrograms", consentPrograms.toImmutable());

            let isOpen = otherInfoModel.get("ConsentProgramPopup");

            otherInfoModel = otherInfoModel.set("ConsentProgramPopup", !isOpen);
            otherInfoModel = otherInfoModel.set("IsModifyingConsentProgram", false);
            otherInfoModel = otherInfoModel.set("SelectedConsentProgramDidntRespond", true);
            otherInfoModel = otherInfoModel.set("HasConsentedOrRefusedOrDidntRespondDate", DateTime.nowDeprecated());
            otherInfoModel = otherInfoModel.set("HasConsentedOrRefusedOrDidntRespondDateText", DateTime.nowMoment().format("YYYY-MM-DD"));

            store.PatientProfileOtherIdentificationModels[response.PatientId] = otherInfoModel;

            sessionStore.showGlobalOverlay(true);
        }
    }

    private addConsentProgram(response: ServerEvent, store: PatientStore): void {
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[response.PatientId];

        otherInfoModel = otherInfoModel.set("ConsentProgramPopup", false);
        otherInfoModel = this.clearConsentPopup(otherInfoModel);

        store.PatientProfileOtherIdentificationModels[response.PatientId] = otherInfoModel;
        sessionStore.hideGlobalOverlay();

        store.ActionsProfile.loadConsentProgramHistory(response.PatientId);
    }

    private loadConsentProgramHistory(response: ServerEvent, store: PatientStore): void {
        if (cacheStore.ClinicSettings.EnableConsentProgramHistory) {
            var responseData: IConsentProgramHistory[] = response.Data["ConsentProgramHistory"];

            store.PatientModels[response.PatientId].ConsentProgramHistory = responseData;

            store.PatientProfileOtherIdentificationModels[response.PatientId] = store.PatientProfileOtherIdentificationModels[response.PatientId]
                .set("ConsentProgramHistory", responseData.toImmutable());

            store.PatientProfileOtherIdentificationUndoModels[response.PatientId] = store.PatientProfileOtherIdentificationModels[response.PatientId];
        }
        else {
            var result: PatientConsentModel[] = response.Data["PatientConsents"];

            let otherInfoModel = store.PatientProfileOtherIdentificationModels[response.PatientId];
            otherInfoModel = otherInfoModel.set("PatientConsentsList", result.toImmutable() ?? [].toImmutable());

            store.PatientProfileOtherIdentificationModels[response.PatientId] = otherInfoModel;
            store.PatientProfileOtherIdentificationUndoModels[response.PatientId] = otherInfoModel;
        }
    }

    private clearConsentPopup(model: IPatientOtherInformationModel): IPatientOtherInformationModel {
        model = model.set("SelectedConsentProgram", null);
        model = model.set("SelectedConsentProgramId", null);
        model = model.set("SelectedConsentProgramDocGiven", false);
        model = model.set("SelectedConsentProgramHasRefused", false);
        model = model.set("SelectedConsentProgramHasConsented", false);
        model = model.set("SelectedConsentProgramDidntRespond", false);
        model = model.set("SelectedConsentProgramHasRevoked", false);
        model = model.set("HasConsentedOrRefusedOrDidntRespondDate", null);
        model = model.set("HasConsentedOrRefusedOrDidntRespondDateText", "");
        model = model.set("HasRevokedDate", null);
        model = model.set("HasRevokedDateText", "");
        model = model.set("DocGivenDate", null);
        model = model.set("DocGivenDateText", "");

        return model;
    }

    private loadConsentProgramsForEdit(response: ServerEvent, store: PatientStore): void {
        let consentProgramsResponse: ConsentProgramsData[] = response.Data["ConsentPrograms"];
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[response.PatientId];

        if (consentProgramsResponse.length > 0) {
            let consentPrograms = consentProgramsResponse.map(c => new ConsentPrograms(c));
            otherInfoModel = otherInfoModel.set("ConsentPrograms", consentPrograms.toImmutable());

            let isOpen = otherInfoModel.get("ConsentProgramPopup");

            otherInfoModel = otherInfoModel.set("ConsentProgramPopup", !isOpen);

            let modifyingId = otherInfoModel.get("ModifyingProgramId");
            let modifyingConsent = otherInfoModel.get("PatientConsentsList").toArray().filter(n => n.Id === modifyingId)[0];

            let consentProgram = consentPrograms.filter(b => b.model.Id === modifyingConsent.ConsentProgramId)[0];

            otherInfoModel = otherInfoModel.set("SelectedConsentProgram", consentProgram);
            otherInfoModel = otherInfoModel.set("SelectedConsentProgramId", consentProgram.model.Id);

            otherInfoModel = otherInfoModel.set("SelectedConsentProgramDocGiven", modifyingConsent.IsDocGiven);
            otherInfoModel = otherInfoModel.set("DocGivenDate", modifyingConsent.DocGivenDate);
            otherInfoModel = otherInfoModel.set("DocGivenDateText", moment(modifyingConsent.DocGivenDate).format("YYYY-MM-DD"));

            if (modifyingConsent.HasRevoked != null) {
                otherInfoModel = otherInfoModel.set("SelectedConsentProgramHasRevoked", modifyingConsent.HasRevoked);
                otherInfoModel = otherInfoModel.set("HasRevokedDate", modifyingConsent.RevokedDate);
                otherInfoModel = otherInfoModel.set("HasRevokedDateText", moment(modifyingConsent.RevokedDate).format("YYYY-MM-DD"));
            }
            else {
                otherInfoModel = otherInfoModel.set("SelectedConsentProgramHasRevoked", false);
            }

            if (modifyingConsent.HasConsentment == null) {
                otherInfoModel = otherInfoModel.set("SelectedConsentProgramDidntRespond", true);
            }
            else if (modifyingConsent.HasConsentment) {
                otherInfoModel = otherInfoModel.set("SelectedConsentProgramHasConsented", true);
            }
            else {
                otherInfoModel = otherInfoModel.set("SelectedConsentProgramHasRefused", true);
            }

            if (modifyingConsent.ConsentDate != null) {
                otherInfoModel = otherInfoModel.set("HasConsentedOrRefusedOrDidntRespondDate", modifyingConsent.ConsentDate);
                otherInfoModel = otherInfoModel.set("HasConsentedOrRefusedOrDidntRespondDateText", moment(modifyingConsent.ConsentDate).format("YYYY-MM-DD"));
            }

            otherInfoModel = otherInfoModel.set("IsModifyingConsentProgram", true);

            store.PatientProfileOtherIdentificationModels[response.PatientId] = otherInfoModel;
            sessionStore.showGlobalOverlay(true);
        }
    }

    private updateConsentProgram(response: ServerEvent, store: PatientStore): void {
        let otherInfoModel = store.PatientProfileOtherIdentificationModels[response.PatientId];

        otherInfoModel = otherInfoModel.set("ConsentProgramPopup", false);
        otherInfoModel = this.clearConsentPopup(otherInfoModel);

        store.PatientProfileOtherIdentificationModels[response.PatientId] = otherInfoModel;
        sessionStore.hideGlobalOverlay();

        store.ActionsProfile.loadConsentProgramHistory(response.PatientId);
    }

    private deleteProfilePic(response: ServerEvent, store: PatientStore): void {
        if (response.Data.Success) {
            var uiModel = store.PatientUIModels[response.PatientId];
            var bannerModel = store.PatientBannerModels[response.PatientId];
            var model = bannerModel.get("Model");
            model.ProfilePicture = null;
            bannerModel = bannerModel.set("Model", model);
            uiModel = uiModel.set("ShowProfileImagePopup", false);
            store.PatientBannerModels[response.PatientId] = bannerModel;
            store.PatientUIModels[response.PatientId] = uiModel;
            sessionStore.hideGlobalOverlay();
        }
    }

    private handlePrint(response: ServerEvent): void {
        if (!response.Data.HasContent) {
            sessionStore.showPopup(cacheStore.GetString("PrintOrFax_AllUnprintable"));
        }

        else if (response.Data.FailedFiles && response.Data.FailedFiles.length) {
            sessionStore.showPopup({
                content: [`${cacheStore.GetString("PrintOrFax_UnsupportedFileTypes")}:`].concat(response.Data.FailedFiles),
                showCancel: true,
                autoWidth: true,
                contentStyle: "list-after-first",
                okAction: () => PrintHelper.ShowPrintPreviewFileName(response.Data.FileName)
            });
        }

        else
            PrintHelper.ShowPrintPreviewFileName(response.Data.FileName);
    }
}

export { PatientProfileResponseHandler };
